/*!
 * Created by netlogix GmbH & Co. KG
 *
 * @copyright netlogix GmbH & Co. KG
 */

import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { AbstractControl, FormArray, FormBuilder, UntypedFormControl, UntypedFormGroup, ValidatorFn, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA } from '@angular/material/dialog';
import * as UsersActions from '@app/user/store/users.actions';
import { getCurrentUser, getIsUserActionPending, getUserPersistViolations } from '@app/user/store/users.selectors';
import { State } from '@core/store';
import { Store } from '@ngrx/store';
import { ConstraintViolation, User, UserRole } from '@shared/models';
import { Observable, of, Subscription } from 'rxjs';
import { filter, map, tap } from 'rxjs/operators';

@Component({
    selector: 'app-user-detail',
    templateUrl: './user-detail.component.html',
    styleUrls: ['./user-detail.component.scss'],
    standalone: false
})
export class UserDetailComponent implements OnInit, OnDestroy {
  user$: Observable<User>;
  isActionPending$: Observable<boolean>;
  userRoles = [
    UserRole.Admin,
    UserRole.User,
    UserRole.Personnel,
    UserRole.Reader
  ];

  private serverErrors = new Map<string, string>();

  userFormGroup = new UntypedFormGroup({
    firstName: new UntypedFormControl('', [Validators.required, this.serverError('firstName')]),
    lastName: new UntypedFormControl('', [Validators.required, this.serverError('lastName')]),
    email: new UntypedFormControl('', [Validators.email, Validators.required, this.serverError('email')]),
    plainPassword: new UntypedFormControl('', [Validators.required, this.serverError('plainPassword')]),
    roles: new UntypedFormControl([], this.serverError('roles'))
  });

  private violationsSubscription: Subscription;
  private formChangesSubscription: Subscription;

  constructor(
    private store: Store<State>,
    @Inject(MAT_DIALOG_DATA) public parameters: { userId: string }
  ) {
  }

  ngOnInit(): void {
    this.store.dispatch(new UsersActions.LoadUser(this.parameters.userId));

    this.isActionPending$ = this.store.select(getIsUserActionPending);
    this.violationsSubscription = this.store.select(getUserPersistViolations).pipe(
      filter(violations => violations.length > 0)
    ).subscribe(violations => this.applyViolations(violations));
    this.formChangesSubscription = this.userFormGroup.valueChanges
      .subscribe(() => this.clearServerErrors());
    this.user$ = this.store.select(getCurrentUser).pipe(
      tap(user => {
        if (user.hasOwnProperty('@id')) {
          this.userFormGroup.get('plainPassword').setValidators(null);
        }
        // Ohne emitEvent wuerde der Patch die Backend-Meldungen verwerfen.
        return this.userFormGroup.patchValue(user, {emitEvent: false});
      })
    );
  }

  persistUser(): void {
    this.store.dispatch(new UsersActions.PersistUser(this.userFormGroup.value));
  }

  ngOnDestroy(): void {
    this.violationsSubscription?.unsubscribe();
    this.formChangesSubscription?.unsubscribe();
  }

  private applyViolations(violations: ConstraintViolation[]): void {
    this.serverErrors.clear();
    violations
      .filter(violation => !!this.userFormGroup.get(violation.propertyPath))
      .forEach(violation => this.serverErrors.set(violation.propertyPath, violation.message));

    Object.entries(this.userFormGroup.controls).forEach(([name, control]) => {
      control.updateValueAndValidity({emitEvent: false});

      if (this.serverErrors.has(name)) {
        control.markAsTouched();
      }
    });
  }

  private clearServerErrors(): void {
    if (0 === this.serverErrors.size) {
      return;
    }

    this.serverErrors.clear();
    Object.values(this.userFormGroup.controls)
      .forEach(control => control.updateValueAndValidity({emitEvent: false}));
  }

  private serverError(controlName: string): ValidatorFn {
    return () => this.serverErrors.has(controlName)
      ? {server: this.serverErrors.get(controlName)}
      : null;
  }
}
